// Task #3822 — viewer read-only di una conversazione di progetto di un ALTRO
// membro: i membri vedono i contenuti del progetto, ma scrivere resta
// prerogativa del proprietario della conversazione (niente co-editing).
import React, { useEffect, useState } from 'react';
import { ChevronLeft, Eye, Loader2 } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import type { PaProjectConversationMessage } from '@easyflow/shared';
import { formatRelativeTime } from '../chatFormat';
import { projectsApi } from './useProjects';

interface Props {
  projectId: string;
  conversationId: string;
  conversationTitle: string | null;
  authorName: string | null;
  onBack: () => void;
}

export const ProjectConversationViewer: React.FC<Props> = ({
  projectId, conversationId, conversationTitle, authorName, onBack,
}) => {
  const [messages, setMessages] = useState<PaProjectConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    projectsApi.conversationMessages(projectId, conversationId)
      .then((msgs) => { if (!cancelled) setMessages(msgs); })
      .catch((err) => {
        console.error('[ProjectConversationViewer] load failed', err);
        if (!cancelled) setError('Impossibile caricare i messaggi');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, conversationId]);

  return (
    <div className="flex h-full flex-col">
      <div className="px-2 py-2 flex items-center gap-1.5 bg-card border-b border-border shrink-0">
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onBack} title="Indietro">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-xs font-semibold truncate" title={conversationTitle ?? undefined}>
            {conversationTitle || 'Senza titolo'}
          </div>
          <div className="text-[10px] text-muted-foreground truncate">
            <Eye className="inline h-2.5 w-2.5 mr-0.5" />
            sola lettura{authorName ? ` · di ${authorName}` : ''}
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="px-2 py-2 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && <div className="text-xs text-red-600 px-1">{error}</div>}
          {!loading && !error && messages.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">Nessun messaggio</div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg px-2.5 py-1.5 text-xs whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-violet-50 text-violet-950 ml-4'
                  : 'bg-muted text-foreground mr-4'
              }`}
            >
              <div className="text-[9px] uppercase tracking-wide text-muted-foreground mb-0.5">
                {m.role === 'user' ? (authorName || 'Utente') : 'Assistente'} · {formatRelativeTime(m.createdAt)}
              </div>
              {m.content || <span className="italic text-muted-foreground">(vuoto)</span>}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
